import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { products } from "../data/products";
import { Plus, Minus, ArrowLeft, ShoppingBag } from "lucide-react";
import { useCart } from "react-use-cart";
import { formatPrice } from "../utils/helper";
import { useProductsContext } from "../context/ProductContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const { product, getProductById } = useProductsContext();
  const [selectedVariant, setSelectedVariant] = useState(null);

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
  };

  useEffect(() => {
    getProductById(id);
  }, [id]);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold mb-4 text-[#7a6e5f]">
          Product not found
        </h2>
        <button
          onClick={() => navigate("/products")}
          className="text-[#809080] hover:underline inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </button>
      </div>
    );
  }

  const handleAddToCart = () => {
  let variantToUse = selectedVariant;

  if (!variantToUse && product.variants.length === 1) {
    variantToUse = product.variants[0];
  }

  if (!variantToUse && product.variants.length === 0) {
    alert("This product has no variant configured. Please fix this in the backend.");
    return;
  }

  addItem(
    {
      id: variantToUse.id,               
      variantId: variantToUse.id,        
      name: `${product.name} - ${variantToUse.variant_name || "Default"}`,
      price: product.price,
      image: variantToUse.image || product.image,
      quantity,
      variants: product.variants || [],
    },
    quantity
  );

  setQuantity(1);
};


  return (
    <div className="bg-[#fef8f5]">
      <div
        className="relative h-[40vh] bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1588776814546-ec7d73834fe9?q=80&w=1480&auto=format&fit=crop')",
        }}
      >
        <div className="absolute inset-0 bg-[#fef8f5] opacity-90" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="text-4xl font-serif font-bold text-[#7a6e5f]">
            {product.name}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16 bg-[#fef8f5]">
        <button
          onClick={() => navigate("/products")}
          className="mb-8 text-[#809080] hover:underline inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Product Image */}
          <div className="aspect-square overflow-hidden rounded-xl shadow-md border border-[#f5eee6]">
            <img
              src={`${process.env.REACT_APP_API_URL}/storage/${product.image}`}
              alt={product.name}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
          </div>

          {/* Product Details */}
          <div className="text-[#7a6e5f]">
            <h1 className="text-3xl font-serif font-bold mb-3">
              {product.name}
            </h1>
            <p className="text-2xl text-[#809080] font-medium mb-6">
              {formatPrice(product.price)}
            </p>

            <div className="mb-6">
              <h2 className="font-semibold mb-2">Description</h2>
              <p className="text-base opacity-80 leading-relaxed">
                {product.description}
              </p>
            </div>

            {product.variants.length > 0 ? (
              <div className="mb-8">
                <h2 className="font-semibold mb-4">Select a Variant</h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                  {product.variants.map((variant) => {
                    const isSelected = selectedVariant?.id === variant.id;
                    return (
                      <button
                        key={variant.id}
                        onClick={() => handleVariantSelect(variant)}
                        className={`flex flex-col items-center justify-center p-2 rounded-lg border transition-all duration-300 hover:shadow-sm hover:border-[#d6a89e] ${
                          isSelected
                            ? "border-2 border-[#809080] bg-[#f5eee6]"
                            : "border border-[#f5eee6]"
                        }`}
                      >
                        <img
                          src={`${process.env.REACT_APP_API_URL}/storage/${variant.image}`}
                          alt={variant.name}
                          className="w-16 h-16 object-cover rounded-md mb-1"
                        />
                        <span className="text-sm text-[#7a6e5f] text-center">
                          {variant.variant_name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="mb-8 text-[#7a6e5f] bg-[#f5eee6] p-4 rounded-md text-sm">
                This product comes in one perfect version—no need to choose a
                variant ✨
              </div>
            )}

            <div className="mb-8">
              <h2 className="font-semibold mb-4">Quantity</h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity((prev) => Math.max(prev - 1, 1))}
                  className="p-2 rounded-full border hover:bg-[#f5eee6] transition"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span className="text-xl font-medium w-12 text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity((prev) => prev + 1)}
                  className="p-2 rounded-full border hover:bg-[#f5eee6] transition"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            <button
              onClick={handleAddToCart}
              className="w-full bg-[#809080] text-white py-4 rounded-md hover:bg-[#6d7d6d] transition-colors flex items-center justify-center gap-2 font-medium"
            >
              <ShoppingBag className="w-5 h-5" />
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
